import config from "../../../config";
import {
  decryptText,
  decryptWithPrivateKey,
} from "../../../Utils/OstreonEncryption";
import {
  decryptDataEntries,
  decryptExposureOptions,
  decryptFiles,
  decryptRecords,
} from "../../../Utils/dataEncryption";
import { decryptBase64file } from "../../../Utils/UploadFile";
import { decryptPartnerFiles } from "../../../Utils/dataEncryption";

const base_url = config.base_url;

/*
  One customer's shared data, decrypted for the partner looking at it.

  Three steps: ask the server what this partner is allowed to see, open the
  customer's key with the partner's private key, then decrypt what came back.

  The decryptors here are deliberately NOT the app's own decryptAsset /
  decryptBeneficiary / decryptTrustee. Those rebuild a complete record and
  assume every field is present, because for the customer's own view it always
  is. Here most fields are absent by design -- the server sends only what a flag
  allows -- so this states, field by field, exactly what a partner can be shown.
  That the list appears twice is the point: the server's copy is the one that
  enforces it, and this one fails loudly if they ever drift, because a field the
  server stops sending simply reads empty here.

  Nothing in this file can widen access. It decrypts what arrived and no more.
*/

const post = (path, body) =>
  fetch(base_url + path, {
    method: "POST",
    mode: "cors",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-type": "application/x-www-form-urlencoded",
      credentials: "include",
    },
    credentials: "include",
    body,
  });

/*
  A client-encrypted scalar, or "" when it was not sent. decryptText returns the
  `""` sentinel for an absent value rather than throwing, so one withheld field
  cannot take a whole record with it.
*/
const dec = async (key, value) => {
  if (!value) return "";
  try {
    const parsed = JSON.parse(await decryptText(key, value));
    return parsed === null || parsed === undefined ? "" : parsed;
  } catch (error) {
    return "";
  }
};

/*
  Server-side encrypted fields arrive already readable -- mongoose decrypts them
  on the way out -- so they only need normalising, not decrypting.
*/
const asArray = (value) => (Array.isArray(value) ? value : []);

/*
  Several asset fields are stored as a JSON STRING rather than an array --
  asset_files and trustees among them. asArray on the raw value is not a
  no-op there: a string is not an array, so it returns [] and the field is
  silently lost. This parses first, and still ends at [] if it cannot.
*/
const parseArray = (value) => {
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

/*
  What a partner may CHANGE, and what they have already asked to change.

  Carried through unread. These are the one part of a shared record that is
  not the customer's data: the edit flags are the customer's answer to "may
  they?", resolved by the server (it sends `edit_x: true` only when the
  matching view flag is on as well), and `partner_changes` is this partner's
  own queued work coming back to them.

  A function rather than five lines repeated three times, because the
  decryptors below build each record field by field and anything not named
  is dropped silently. That is exactly how this went missing: the flags
  arrived on the payload, were never copied onto the object, and every modal
  concluded the partner had no permission -- read-only fields, no Save.
  One place to add a field is one place to forget it.

  The values inside `partner_changes` stay encrypted here; `readProposals`
  opens them where they are shown.
*/
const ASSET_EDIT_FLAGS = [
  "edit_description",
  "edit_files",
  "edit_evaluations",
  "edit_beneficiaries",
  "edit_trusted_advisor",
  /*
    And the VIEW half of each, which the person lists below have carried for
    a while and this one did not. Dropping them here is the sixth-site bug
    again: the server sends them, nothing here names them, and the asset
    modal concludes the partner may see nothing -- with no symptom pointing
    at the cause.
  */
  "share_description",
  "share_files",
  "share_evaluations",
  "share_beneficiaries",
  "share_trusted_advisor",
];
/*
  A beneficiary's five. No edit_contact_with_partner: their contact details
  are view-only, so there is no such flag to carry.
*/
const BENEFICIARY_EDIT_FLAGS = [
  "edit_with_partner",
  "edit_message_with_partner",
  "edit_picture_with_partner",
  "edit_trustees_with_partner",
  "edit_assets_with_partner",
  /*
    And the VIEW half of each row. Carried for the same reason the server
    sends it: a row that is shared and empty arrives looking exactly like a
    row that was never shared, and the screen has to tell them apart.
  */
  "share_contact_with_partner",
  "share_message_with_partner",
  "share_picture_with_partner",
  "share_trustees_with_partner",
  "share_assets_with_partner",
];
/*
  An advisor's four. No edit_contact_with_partner here either: their contact
  details are view-only, as a beneficiary's are.
*/
const PERSON_EDIT_FLAGS = [
  "edit_with_partner",
  "edit_message_with_partner",
  "edit_assets_with_partner",
  "edit_beneficiaries_with_partner",
  // ...and the view half; see the beneficiary list above.
  "share_contact_with_partner",
  "share_message_with_partner",
  "share_assets_with_partner",
  "share_beneficiaries_with_partner",
];

const permissions = (flags) => (record) => {
  const out = { partner_changes: asArray(record?.partner_changes) };
  for (const flag of flags) out[flag] = record?.[flag] === true;
  return out;
};
const assetPermissions = permissions(ASSET_EDIT_FLAGS);
const personPermissions = permissions(PERSON_EDIT_FLAGS);
const beneficiaryPermissions = permissions(BENEFICIARY_EDIT_FLAGS);

/*
  A profile picture, as the record's owner would see it.

  Not a field: the image lives on disk and the server reads it back as
  base64 (getProfileAvatar), already encrypted under the customer's key --
  so it arrives as ciphertext and has to be opened like anything else they
  typed. Sent as-is, the partner's copy drew nothing at all, which looked
  exactly like the sharing flag not working.

  The leading character is the test for which, and it is the same test the
  customer's own decryptor makes (Utils/dataEncryption, decryptBeneficiary):
  "/" and "i" are the base64 signatures of a plain JPEG and PNG, so a value
  starting with "/" is already readable and anything else is opened. A
  picture that will not open is dropped rather than shown broken.
*/
const decryptAvatar = async (key, value) => {
  if (!value) return null;
  if (value[0] === "/") return value;
  try {
    return (await decryptBase64file(key, value)) || null;
  } catch (error) {
    return null;
  }
};

const decryptSharedAsset = async (key, asset) => ({
  ...assetPermissions(asset),
  asset_id: asset.asset_id,
  pending_approval: asset.pending_approval === true,
  asset_name: await dec(key, asset.asset_name),
  asset_type: await dec(key, asset.asset_type),
  description: await dec(key, asset.description),

  // Evaluations. Absent unless share_evaluations was on.
  asset_value: await dec(key, asset.asset_value),
  currency: await dec(key, asset.currency),
  evaluation_date: await dec(key, asset.evaluation_date),
  financial_exposure: await dec(key, asset.financial_exposure),
  /*
    undefined when the server withheld it, rather than false. Without the
    distinction the table read a withheld value as "No" -- telling the partner
    the asset is not liquid, which the customer never said.
  */
  is_liquid:
    asset.is_liquid === undefined
      ? undefined
      : (await dec(key, asset.is_liquid)) === true,
  has_associated_debt: (await dec(key, asset.has_associated_debt)) === true,
  debt_type: await dec(key, asset.debt_type),
  debt_amount: await dec(key, asset.debt_amount),
  debt_currency: await dec(key, asset.debt_currency),
  debt_annual_interest: await dec(key, asset.debt_annual_interest),
  debt_payoff_date: await dec(key, asset.debt_payoff_date),
  /*
    The evaluation history and the other-owners block. The server sends these
    only when share_evaluations is on, and they were being thrown away here --
    decrypted to empty regardless -- so a customer who shared their evaluations
    still showed a partner nothing but the headline figures.

    The app's own decryptors are used for these three because they take the raw
    field and tolerate its absence, returning an empty list. Nothing else in
    this file can, which is why the rest is written out by hand.
  */
  asset_evaluation_records: await decryptRecords(key, asset.asset_evaluation_records),
  asset_debt_records: await decryptRecords(key, asset.asset_debt_records),
  data_entries: await decryptDataEntries(key, asset.data_entries),

  /*
    The documents on the asset, when share_files allowed them.

    Stored as a JSON string of {id, name, size, lastModified} whose three text
    fields are client-encrypted, so it needs parsing AND decrypting. It had
    neither: asArray on a string returns [], so every shared file vanished and
    the partner saw an asset with no documents on it.

    Metadata only. The bytes stay on the server until the partner opens one,
    and arrive still encrypted under the customer's key -- see
    /user/partner/customer/file/download, which streams them without being able
    to read them.
  */
  asset_files:
    asset.asset_files === undefined
      ? undefined
      : await decryptFiles(key, parseArray(asset.asset_files)),

  /*
    The beneficiaries this asset names, behind share_beneficiaries — and only
    those whose own switch is also on. A beneficiary the customer did not share
    is not on this list even when the asset shares its beneficiaries.

    NOT the stored field. That one is client-encrypted, so the server cannot
    filter it and never sends it -- what arrives is a plain list of ids the
    server computed from the other end of the relation (visible_assets) and
    intersected with the beneficiaries it is actually sending. So an id here
    always resolves to a record below.
  */
  beneficiaries: parseArray(asset.beneficiaries),

  /*
    Never sent, and named here so it is obvious they are not merely forgotten.
    `credentials` has no flag at all.
  */
  credentials: "",
  access: [],

  trustees: parseArray(asset.trustees),
});

const decryptSharedBeneficiary = async (key, beneficiary) => ({
  ...beneficiaryPermissions(beneficiary),
  user_id: beneficiary.user_id,
  name: await dec(key, beneficiary.name),
  pending_approval: beneficiary.pending_approval === true,
  /*
    Always true for a beneficiary now: the server sends none but the ones the
    customer shared outright. Read anyway, so the client keeps withholding
    correctly if it ever arrives false again. Advisors below keep the older
    rule, where a name can come from a shared asset alone.
  */
  shared_as_record: beneficiary.shared_as_record !== false,
  relationship: await dec(key, beneficiary.relationship),
  // Server-side encrypted, so already readable.
  priority: String(beneficiary.priority ?? ""),
  is_minor: String(beneficiary.is_minor ?? "false"),
  visible_assets: asArray(beneficiary.visible_assets),
  trustees: asArray(beneficiary.trustees),

  // Contact, and only when the second switch was on.
  email: beneficiary.email || "",
  email_2: beneficiary.email_2 || "",
  email_3: beneficiary.email_3 || "",
  phone_number: beneficiary.phone_number || "",

  /*
    The personal message and the documents under it, behind their own row.
    Both client-encrypted, like everything else the customer typed.
  */
  message: await dec(key, beneficiary.message),
  personal_documents:
    beneficiary.personal_documents === undefined
      ? []
      : await decryptFiles(key, parseArray(beneficiary.personal_documents)),

  /*
    The photo, behind its own row. Absent is null rather than "" -- the tree
    and the modal both read null as "draw initials", and an empty string is
    an image source that fails to load.
  */
  profile_avatar: await decryptAvatar(key, beneficiary.profile_avatar),

  /*
    No row covers any of these, so the server never sends them and they are
    empty here rather than absent -- the pages read them and would crash on
    undefined.

    The date of birth is the one on this list that can never be shared at
    all: there is no flag for it and no row to turn on. The others simply
    have no row yet.
  */
  birth_date: "",
  personal_code: "",
  awareness_status: "false",
  awareness_status_2: "false",
  awareness_status_3: "false",
  status: "",
});

const decryptSharedTrustee = async (key, trustee) => ({
  ...personPermissions(trustee),
  user_id: trustee.user_id,
  name: await dec(key, trustee.name),
  pending_approval: trustee.pending_approval === true,
  /*
    False for an advisor sent only because a shared asset names them. They can
    be shown against that asset -- which is what the asset's own switch says --
    but they are not a record the customer shared, so the advisors tab omits
    them.
  */
  shared_as_record: trustee.shared_as_record !== false,
  email: await dec(key, trustee.email),
  phone_number: await dec(key, trustee.phone_number),
  // The row the panel calls their message. An advisor has no files.
  additional_information: await dec(key, trustee.additional_information),
  /*
    Which assets and beneficiaries they advise on, behind their own rows.

    Server-computed id lists, not stored fields -- the relation lives at the
    other end -- and already intersected with what this partner may see. So
    an id here always resolves to a record they were sent, and the list is
    a PARTIAL one: accepting a change to it merges rather than replaces.
  */
  assets: asArray(trustee.assets),
  beneficiaries: asArray(trustee.beneficiaries),

  /*
    No row covers these, so the server never sends them. The photo is the
    one that can never have a row: a partner does not see an advisor's
    picture, and null is what makes the tree draw initials.
  */
  awareness_status: "false",
  profile_avatar: null,
  /*
    Whether this advisor's fields accept typing at all.

    NOT a permission -- `editable: false` marks the one advisor a customer
    cannot rewrite, the card derived from their legal entity, which is
    injected on the client (Utils/prepareUserFetch) and never appears in a
    shared payload. Every advisor here is one the customer manages.

    It was false, from when a partner could edit nothing, and the modal
    reads it as "no field takes a keystroke": every onChange begins with
    `if (!editable) return`. So a partner GRANTED the name row found the
    field inert and had no way to propose the change they had been allowed
    to make. What they may actually touch is decided per section, by
    editableIf and ViewOnly.
  */
  editable: true,
});

/*
  The shape the app's pages read, filled with what this partner may see and
  neutral values everywhere else.

  Every empty below is a deliberate absence, not a gap waiting to be filled:
  inheritance parameters, the legal card, the plan and the customer's own
  contact details have no sharing flag, so they are not requested and not shown.
  See docs and the whitelist in Routes/PartnerUser.js.
*/
const EMPTY_PARAMETERS = {
  keep_alive_method: [],
  keep_alive_frequency: 0,
  notify_ostreon: false,
  priority_one_delay: 0,
  priority_two_delay: 0,
  self_recovery: 0,
  self_recovery_day: 0,
  next_notification: 0,
  sent_notifications: 0,
  dday_priority_one: 0,
  dday_priority_two: 0,
};

export const buildSharedUserData = async (customer_id, key, payload) => {
  /*
    The customer's own exposure type names, sent only when at least one shared
    asset shares its evaluations. Decrypted with the same helper the customer's
    own client uses, because it is the same field in the same shape -- the
    labels are client-encrypted and the ids are not.

    Needed so the wealth report can put a name on a section. An asset's
    financial_exposure is client-encrypted, so the server cannot resolve the id
    for us; without this list the partner reads "fx_MHENK9..." as a heading.
  */
  const exposureOptions = await decryptExposureOptions(
    key,
    payload?.financial_exposure_custom_options
  );
  const assets = [];
  for (const asset of payload?.assets || []) {
    // eslint-disable-next-line no-await-in-loop
    assets.push(await decryptSharedAsset(key, asset));
  }
  const beneficiaries = [];
  for (const beneficiary of payload?.beneficiaries || []) {
    // eslint-disable-next-line no-await-in-loop
    beneficiaries.push(await decryptSharedBeneficiary(key, beneficiary));
  }
  const trustees = [];
  for (const trustee of payload?.trustees || []) {
    // eslint-disable-next-line no-await-in-loop
    trustees.push(await decryptSharedTrustee(key, trustee));
  }

  return {
    user_id: customer_id,
    customer_full_name: "",
    customer_name: "",
    customer_last_name: "",
    customer_phone_number: "",
    plan: "",
    keep_alive_timeout: 0,
    last_login: 0,
    inheritance_mechanism_status: false,
    /*
      Attention points are computed from the customer's whole record and their
      text names assets and beneficiaries -- including ones this partner was
      never given. An empty list is the only correct value here until they are
      recomputed over the shared subset.
    */
    issues: [],
    legal: [],
    assets,
    beneficiaries,
    trustees,
    inheritance_parameters: EMPTY_PARAMETERS,
    financial_exposure_custom_options: exposureOptions,
    /*
      null, not []. Asset Management reads this as "the columns to show", and
      falls back to its default set only when the value is absent -- an empty
      ARRAY is truthy, so it was being read as "show no columns", which drew a
      table of rows with nothing in them.
    */
    asset_management_presented_fields: null,
    asset_type_custom_options: [],
    asset_owner_custom_options: [],
    /*
      The files the customer uploaded expressly for this partner.

      No flag gates these and there is none to add: uploading one here is
      the act of sharing it. Opened with the customer's key like
      everything else, and read-only on this side -- the panel offers the
      partner no upload control and no remove handle, and the writers
      behind both are stubbed in this view anyway.
    */
    partner_shared_files: await decryptPartnerFiles(
      key,
      payload?.partner_shared_files
    ),
  };
};

/*
  Create one record on the customer's account, encrypted under their key.

  It goes to an append-only endpoint that forces pending_approval and stamps the
  partner's id, so the customer decides whether it ever counts. Nothing here can
  change or remove anything they already had.
*/
export const createForCustomer = async (customer_id, key, kind, record) => {
  try {
    const res = await post(
      "user/partner/customer/create",
      new URLSearchParams({
        customer_id,
        kind,
        record: JSON.stringify(record),
      }).toString()
    );
    /*
      The id comes back because the server is the one that chose it -- see
      the endpoint. A record created from inside another modal is attached
      by it, so losing it here would mean the attachment could not be made.
    */
    if (res.status === 200) {
      const body = await res.json().catch(() => null);
      return { ok: true, id: body?.id || null };
    }
    /*
      Carry the reason back. A bare false told the caller nothing, so it could
      not say why -- and the one caller that showed a progress modal simply left
      it up.
    */
    const detail = await res.text().catch(() => "");
    return { ok: false, status: res.status, detail };
  } catch (error) {
    console.log(error);
    return { ok: false, status: 0, detail: String(error?.message || error) };
  }
};

/*
  Fetch and decrypt. Returns null when there is nothing to show -- no wrap, no
  private key, nothing shared -- which the caller turns into a redirect rather
  than an error, because "not shared" is a normal state, not a failure.

  Resolves to { key, data }: the customer's own key stays with the payload
  because writing a new record back needs it, and it is never stored anywhere
  beyond this component's state.
*/
export const loadSharedCustomer = async (customer_id, partner_private_key) => {
  if (!customer_id) return null;
  // No usable keypair: nothing this partner holds can open anything.
  if (!partner_private_key) return { error: "no_key" };
  try {
    const res = await post(
      "user/partner/customer/shared",
      new URLSearchParams({ customer_id }).toString()
    );
    // 403 is the ordinary "this customer shares nothing with you".
    if (res.status === 403) return null;
    if (res.status !== 200) return { error: "unavailable" };
    const payload = await res.json();
    /*
      Shared, but no wrap of the customer's key. That happens when the customer
      has not signed in since their partner had a keypair -- the wrap is made in
      their browser, and only they can make it.
    */
    if (!payload?.key_wrap) return { error: "no_wrap" };

    /*
      The customer's own key, opened with the partner's private half. This is
      the only place the partner's browser learns it, and it is never stored.
    */
    let key;
    try {
      key = await decryptWithPrivateKey(partner_private_key, payload.key_wrap);
    } catch (error) {
      // The wrap was made for a different keypair than the one we hold.
      console.log(error);
      return { error: "wrong_key" };
    }
    if (!key) return { error: "wrong_key" };

    return {
      key,
      data: await buildSharedUserData(customer_id, key, payload),
    };
  } catch (error) {
    console.log(error);
    return { error: "unavailable" };
  }
};
